import type { MessageKey } from '../i18n';
import type { NormalizedAnalysis, UiRecord } from './types';
import {
  renderImagePreview,
  type ImagePreviewOptions,
  type PreviewState,
  bindImagePreview,
} from './image-preview';
import {
  findingLabel,
  formatBytes,
  formatDate,
  formatNumber,
  formatPercent,
  normalizeAnalysis,
} from './utils';

export interface DetailPanelState {
  preview: PreviewState;
  trimMargin: 0 | 2 | 4 | 8 | 16;
  trimming: boolean;
}

export interface DetailPanelOptions extends ImagePreviewOptions {
  locale: string;
  onTrimMargin: (margin: 0 | 2 | 4 | 8 | 16) => void;
  onTrim: (id: string) => void;
  onReanalyze: (id: string) => void;
}

export function renderDetailPanel(
  record: UiRecord | undefined,
  state: DetailPanelState,
  options: DetailPanelOptions,
): string {
  const t = options.translate;
  if (!record) {
    return `
      <section class="detail-panel detail-panel--empty" aria-labelledby="detail-title" data-testid="detail-panel">
        <div class="section-heading"><h2 id="detail-title">${t('details')}</h2></div>
        <div class="empty-state detail-empty" data-testid="detail-empty-state">
          <span class="empty-state__icon" aria-hidden="true">▣</span>
          <strong>${t('noSelectionTitle')}</strong>
          <p>${t('noSelectionBody')}</p>
        </div>
      </section>
    `;
  }
  if (record.status === 'queued' || record.status === 'analyzing')
    return renderLoading(record, options);
  if (record.status === 'error') return renderError(record, options);

  const analysis = normalizeAnalysis(record);
  return `
    <section class="detail-panel" aria-labelledby="detail-title" data-testid="detail-panel">
      <div class="detail-header">
        <div>
          <span class="detail-header__label">${t('details')}</span>
          <h2 id="detail-title" title="${escapeAttribute(record.file.name)}">${escapeHtml(record.file.name)}</h2>
        </div>
        ${renderPrimaryStatus(analysis, t)}
      </div>

      ${renderImagePreview(record, analysis, state.preview, options)}

      <div class="detail-grid">
        <section class="detail-section" aria-labelledby="alpha-statistics-title">
          <h3 id="alpha-statistics-title">${t('alphaStatistics')}</h3>
          <dl class="stat-list">
            ${stat(t('totalPixels'), formatNumber(analysis.totalPixels, options.locale))}
            ${stat(t('fullyTransparent'), `${formatNumber(analysis.transparentPixels, options.locale)} (${formatPercent(analysis.transparentPercentage)})`)}
            ${stat(t('semiTransparent'), `${formatNumber(analysis.semiTransparentPixels, options.locale)} (${formatPercent(analysis.semiTransparentPercentage)})`)}
            ${stat(t('fullyOpaque'), formatNumber(analysis.opaquePixels, options.locale))}
            ${stat(t('minAlpha'), formatOptionalNumber(analysis.minAlpha, options.locale))}
            ${stat(t('maxAlpha'), formatOptionalNumber(analysis.maxAlpha, options.locale))}
            ${stat(t('averageAlpha'), analysis.averageAlpha === undefined ? t('notAvailable') : analysis.averageAlpha.toFixed(2))}
            ${stat(t('edgeCoverage'), formatPercent(analysis.edgeCoveragePercentage))}
          </dl>
          <p class="inline-note"><span aria-hidden="true">i</span>${t('semiDisclaimer')}</p>
        </section>

        <section class="detail-section" aria-labelledby="png-structure-title">
          <h3 id="png-structure-title">${t('pngStructure')}</h3>
          <dl class="stat-list">
            ${stat(t('imageDimensions'), analysis.width > 0 ? `${analysis.width} × ${analysis.height} px` : t('notAvailable'))}
            ${stat(t('fileSize'), formatBytes(record.file.size))}
            ${stat(t('bitDepth'), formatOptionalNumber(analysis.bitDepth, options.locale))}
            ${stat(t('colorType'), analysis.colorType === undefined ? t('notAvailable') : String(analysis.colorType))}
            ${stat(t('trnsChunk'), analysis.hasTrns === undefined ? t('notAvailable') : analysis.hasTrns ? t('yes') : t('no'))}
            ${stat(t('alphaSupport'), analysis.supportsAlpha === undefined ? t('notAvailable') : analysis.supportsAlpha ? t('yes') : t('no'))}
            ${stat(t('analyzedAt'), formatDate(analysis.analyzedAt, options.locale))}
            ${stat(t('duration'), analysis.durationMs === undefined ? t('notAvailable') : `${Math.round(analysis.durationMs)} ms`)}
          </dl>
        </section>

        <section class="detail-section detail-section--wide" aria-labelledby="findings-title">
          <h3 id="findings-title">${t('findings')}</h3>
          ${renderFindings(analysis, t, options.locale)}
          <div class="heuristic-warning" role="note">
            <span class="status-icon" aria-hidden="true">!</span>
            <div><strong>${t('heuristicTitle')}</strong><p>${t('heuristicDisclaimer')}</p></div>
          </div>
        </section>

        <section class="detail-section detail-section--wide trim-section" aria-labelledby="trim-title">
          <div class="trim-section__heading">
            <div>
              <h3 id="trim-title">${t('trimTitle')}</h3>
              <p>${trimExplanation(analysis, state, t)}</p>
            </div>
            <div class="trim-controls">
              <div class="field field--inline">
                <label for="trim-margin">${t('trimMargin')}</label>
                <select id="trim-margin" data-testid="trim-margin" ${state.trimming ? 'disabled' : ''}>
                  ${[0, 2, 4, 8, 16].map((margin) => `<option value="${margin}" ${state.trimMargin === margin ? 'selected' : ''}>${margin} px</option>`).join('')}
                </select>
              </div>
              <button class="button button--primary" type="button" data-action="trim" data-record-id="${record.id}" data-testid="trim-button" aria-describedby="trim-explanation" ${!analysis.safeToTrim || state.trimming ? 'disabled' : ''}>
                <span aria-hidden="true">↓</span>${state.trimming ? t('trimWorking') : t('trimButton')}
              </button>
            </div>
          </div>
          <div class="margin-readout mono-value" aria-label="${t('transparentPadding')}">${t('paddingValues', { ...analysis.margins })}</div>
        </section>
      </div>
    </section>
  `;
}

function renderLoading(record: UiRecord, options: DetailPanelOptions): string {
  const t = options.translate;
  return `
    <section class="detail-panel" aria-labelledby="detail-title" aria-busy="true" data-testid="detail-panel">
      <div class="detail-header">
        <div><span class="detail-header__label">${t('details')}</span><h2 id="detail-title">${escapeHtml(record.file.name)}</h2></div>
        <span class="status status--working"><span class="working-glyph" aria-hidden="true">↻</span>${t('statusAnalyzing')}</span>
      </div>
      <div class="analysis-loading" data-testid="analysis-loading">
        <div class="skeleton skeleton--preview"></div>
        <div class="analysis-loading__copy">
          <strong>${t('loadingFile', { name: escapeHtml(record.file.name) })}</strong>
          <span>${t('loadingProgress', { progress: Math.round(record.progress) })}</span>
        </div>
        <div class="progress-track" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow="${Math.round(record.progress)}">
          <span style="width:${record.progress}%"></span>
        </div>
        <div class="skeleton-grid"><span class="skeleton"></span><span class="skeleton"></span></div>
      </div>
    </section>
  `;
}

function renderError(record: UiRecord, options: DetailPanelOptions): string {
  const t = options.translate;
  return `
    <section class="detail-panel" aria-labelledby="detail-title" data-testid="detail-panel">
      <div class="detail-header">
        <div><span class="detail-header__label">${t('details')}</span><h2 id="detail-title">${escapeHtml(record.file.name)}</h2></div>
        <span class="status status--error"><span aria-hidden="true">×</span>${t('statusError')}</span>
      </div>
      <div class="error-state" role="alert" data-testid="analysis-error">
        <span class="error-state__icon" aria-hidden="true">×</span>
        <div><strong>${t('statusError')}</strong><p>${escapeHtml(record.error ?? t('unexpectedError'))}</p></div>
        <button class="button button--secondary" type="button" data-action="detail-reanalyze" data-record-id="${record.id}" data-testid="detail-reanalyze"><span aria-hidden="true">↻</span>${t('reanalyzeFile', { name: escapeAttribute(record.file.name) })}</button>
      </div>
    </section>
  `;
}

function renderPrimaryStatus(
  analysis: NormalizedAnalysis,
  t: DetailPanelOptions['translate'],
): string {
  if (analysis.suspected)
    return `<span class="status status--warning"><span aria-hidden="true">!</span>${t('statusSuspected')}</span>`;
  if (analysis.realTransparency)
    return `<span class="status status--success"><span aria-hidden="true">✓</span>${t('statusTransparent')}</span>`;
  return `<span class="status status--neutral"><span aria-hidden="true">×</span>${t('statusOpaque')}</span>`;
}

const findingKeys: Readonly<Record<string, MessageKey>> = {
  REAL_TRANSPARENCY: 'findingRealTransparency',
  FULL_TRANSPARENCY_PRESENT: 'findingFullTransparencyPresent',
  ALPHA_CHANNEL_UNUSED: 'findingAlphaChannelUnused',
  FULLY_OPAQUE: 'findingFullyOpaque',
  SEMI_TRANSPARENT_PIXELS: 'findingSemiTransparentPixels',
  EDGE_CONTENT: 'findingEdgeContent',
  TRANSPARENT_PADDING: 'findingTransparentPadding',
  SUSPECTED_SOLID_BACKGROUND: 'findingSuspectedSolidBackground',
  SUSPECTED_BAKED_CHECKERBOARD: 'findingSuspectedCheckerboard',
  CLEAN_ASSET: 'findingCleanAsset',
};

function renderFindings(
  analysis: NormalizedAnalysis,
  t: DetailPanelOptions['translate'],
  locale: string,
): string {
  if (analysis.findings.length === 0) return `<p class="empty-findings">${t('noFindings')}</p>`;
  return `
    <div class="finding-list">
      ${analysis.findings
        .map(
          (finding) => `
            <article class="finding finding--${finding.suspected ? 'warning' : finding.code === 'CLEAN_ASSET' || finding.code.includes('TRANSPARENCY') ? 'success' : 'neutral'}">
              <span class="status-icon" aria-hidden="true">${finding.suspected ? '!' : finding.code === 'CLEAN_ASSET' || finding.code.includes('TRANSPARENCY') ? '✓' : 'i'}</span>
              <div>
                <div class="finding__heading"><strong>${escapeHtml(findingKeys[finding.code] ? t(findingKeys[finding.code]) : findingLabel(finding.code))}</strong>${finding.confidence === undefined ? '' : `<span class="confidence">${t('confidence', { value: Math.round(finding.confidence) })}</span>`}</div>
                ${findingReason(finding.code, finding.reason, locale, t)}
              </div>
            </article>
          `,
        )
        .join('')}
    </div>
  `;
}

function findingReason(
  code: string,
  reason: string | undefined,
  locale: string,
  t: DetailPanelOptions['translate'],
): string {
  if (locale === 'zh-CN' && code === 'SUSPECTED_SOLID_BACKGROUND') {
    return `<p>${t('reasonSuspectedSolidBackground')}</p>`;
  }
  if (locale === 'zh-CN' && code === 'SUSPECTED_BAKED_CHECKERBOARD') {
    return `<p>${t('reasonSuspectedCheckerboard')}</p>`;
  }
  return reason ? `<p>${escapeHtml(reason)}</p>` : '';
}

function stat(label: string, value: string): string {
  return `<div><dt>${label}</dt><dd class="mono-value">${value}</dd></div>`;
}

function formatOptionalNumber(value: number | undefined, locale: string): string {
  return value === undefined ? 'N/A' : formatNumber(value, locale);
}

function trimExplanation(
  analysis: NormalizedAnalysis,
  state: DetailPanelState,
  t: DetailPanelOptions['translate'],
): string {
  if (state.trimming) return `<span id="trim-explanation">${t('trimWorking')}</span>`;
  if (analysis.trimDisabledReason === 'pending')
    return `<span id="trim-explanation">${t('trimPending')}</span>`;
  if (analysis.trimDisabledReason === 'no-padding')
    return `<span id="trim-explanation">${t('trimNoPadding')}</span>`;
  if (analysis.trimDisabledReason === 'no-content')
    return `<span id="trim-explanation">${t('trimNoContent')}</span>`;
  return `<span id="trim-explanation">${t('trimReady')}</span>`;
}

export function bindDetailPanel(
  container: HTMLElement,
  state: DetailPanelState,
  options: DetailPanelOptions,
): () => void {
  const cleanupPreview = bindImagePreview(container, state.preview, options);
  container
    .querySelector<HTMLSelectElement>('#trim-margin')
    ?.addEventListener('change', (event) => {
      options.onTrimMargin(
        Number((event.currentTarget as HTMLSelectElement).value) as 0 | 2 | 4 | 8 | 16,
      );
    });
  container
    .querySelector<HTMLButtonElement>('[data-action="trim"]')
    ?.addEventListener('click', (event) => {
      options.onTrim((event.currentTarget as HTMLButtonElement).dataset.recordId ?? '');
    });
  container
    .querySelector<HTMLButtonElement>('[data-action="detail-reanalyze"]')
    ?.addEventListener('click', (event) => {
      options.onReanalyze((event.currentTarget as HTMLButtonElement).dataset.recordId ?? '');
    });
  return cleanupPreview;
}

function escapeHtml(value: string): string {
  const span = document.createElement('span');
  span.textContent = value;
  return span.innerHTML;
}

function escapeAttribute(value: string): string {
  return escapeHtml(value).replaceAll('`', '&#96;');
}
